// src/hooks/useSessionStats.ts
// =====================================================================
// HOOK ISOLADO: SISTEMA DE ESTATÍSTICAS DA SESSÃO v2.3
//
// BUGS CORRIGIDOS nesta versão (em cima dos anteriores):
//
// BUG 5 — cardsMasteredToday não era incremental:
//   ANTES: enviava o total acumulado da sessão inteira a cada registro.
//   O backend faz += nos dois campos, então com 2 registros no dia
//   o valor dobrava (ex: 3 dominados virava 6 no banco).
//   DEPOIS: lastRecordedMasteredRef rastreia o valor já enviado.
//   Cada registro envia apenas o delta (igual ao que já era feito
//   com cardsStudied).
//
// BUG 6 — accuracy ultrapassava 100%:
//   ANTES: accuracy = goodRatings(via Set de cardIds únicos) / totalRatings(via Set).
//   O Set conta apenas a ÚLTIMA nota de cada card único — correto para
//   mostrar na sidebar, mas incorreto para enviar ao backend.
//   Como cardsStudied conta TODAS as respostas (incluindo repetições
//   do mesmo card), os denominadores divergiam e o acumulado no banco
//   ficava inconsistente entre sessões.
//   DEPOIS: accuracy usa sessionStats.cardsStudied (total de respostas,
//   incluindo repetições) como denominador, e conta como "boa" qualquer
//   resposta com rating >= 4, independente de ser repetição ou não.
//   Isso garante que accuracy seja sempre 0–100 e seja consistente
//   com o cardsStudied enviado.
// =====================================================================

import { useState, useEffect, useRef, useCallback } from 'react';

// =====================================================================
// INTERFACES
// =====================================================================

export interface SessionStats {
    cardsStudied: number;
    cardsMasteredToday: number;
    cardsLearned: number;
    cardsReviewed: number;
    ratings: {
        1: { count: number; cardIds: Set<string> };
        2: { count: number; cardIds: Set<string> };
        3: { count: number; cardIds: Set<string> };
        4: { count: number; cardIds: Set<string> };
        5: { count: number; cardIds: Set<string> };
    };
    startTime: number;
    // ✅ NOVO: total de respostas "boas" (rating >= 4), conta repetições
    goodAnswersTotal: number;
}

export interface SessionStatsConfig {
    deckId: string;
    subdeckId: string;
    isReviewMode?: boolean;
    isSavedMode?: boolean;
    initialRatingDistribution?: {
        rating1: number;
        rating2: number;
    };
}

// =====================================================================
// HOOK PRINCIPAL
// =====================================================================

export function useSessionStats(config: SessionStatsConfig) {
    const {
        deckId,
        subdeckId,
        isReviewMode = false,
        isSavedMode = false,
        initialRatingDistribution
    } = config;

    // ===== ESTADO =====
    const [sessionStats, setSessionStats] = useState<SessionStats>({
        cardsStudied: 0,
        cardsMasteredToday: 0,
        cardsLearned: 0,
        cardsReviewed: 0,
        ratings: {
            1: { count: 0, cardIds: new Set<string>() },
            2: { count: 0, cardIds: new Set<string>() },
            3: { count: 0, cardIds: new Set<string>() },
            4: { count: 0, cardIds: new Set<string>() },
            5: { count: 0, cardIds: new Set<string>() }
        },
        startTime: Date.now(),
        goodAnswersTotal: 0
    });

    // ===== REFs ESTÁVEIS =====
    const sessionStatsRef = useRef(sessionStats);
    const configRef = useRef({ deckId, subdeckId, isSavedMode });

    const lastRecordedAtRef = useRef<number>(Date.now());
    const lastRecordedCardsRef = useRef<number>(0);
    // ✅ NOVO: rastreia quantos "dominados" já foram enviados ao backend
    const lastRecordedMasteredRef = useRef<number>(0);
    // ✅ NOVO: rastreia quantas respostas boas já foram enviadas (para accuracy incremental)
    const lastRecordedGoodRef = useRef<number>(0);

    const isRecordingRef = useRef(false);

    // ===== SINCRONIZAR REFS =====
    useEffect(() => {
        sessionStatsRef.current = sessionStats;
    }, [sessionStats]);

    useEffect(() => {
        configRef.current = { deckId, subdeckId, isSavedMode };
    }, [deckId, subdeckId, isSavedMode]);

    // =====================================================================
    // INICIALIZAR DISTRIBUIÇÃO (Modo Revisão)
    // =====================================================================

    useEffect(() => {
        if (isReviewMode && initialRatingDistribution) {
            setSessionStats(prev => ({
                ...prev,
                ratings: {
                    1: { count: initialRatingDistribution.rating1 || 0, cardIds: new Set<string>() },
                    2: { count: initialRatingDistribution.rating2 || 0, cardIds: new Set<string>() },
                    3: { count: 0, cardIds: new Set<string>() },
                    4: { count: 0, cardIds: new Set<string>() },
                    5: { count: 0, cardIds: new Set<string>() }
                }
            }));
        }
    }, [isReviewMode, initialRatingDistribution]);

    // =====================================================================
    // ATUALIZAR STATS APÓS RESPOSTA
    // =====================================================================

    const updateStats = useCallback((
        cardId: string,
        rating: number,
        cardStatus: 'new' | 'learning' | 'reviewing' | 'mastered' | 'saved' | 'custom',
        previousRating: number | null
    ) => {
        console.log('🎯 [STATS] updateStats:', { cardId, rating, cardStatus });

        setSessionStats(prev => {
            const updatedRatings = { ...prev.ratings };

            // Remover da nota anterior se houver
            if (previousRating !== null && previousRating >= 1 && previousRating <= 5) {
                updatedRatings[previousRating as 1 | 2 | 3 | 4 | 5].cardIds.delete(cardId);
                updatedRatings[previousRating as 1 | 2 | 3 | 4 | 5].count =
                    updatedRatings[previousRating as 1 | 2 | 3 | 4 | 5].cardIds.size;
            }

            // Adicionar na nova nota
            updatedRatings[rating as 1 | 2 | 3 | 4 | 5].cardIds.add(cardId);
            updatedRatings[rating as 1 | 2 | 3 | 4 | 5].count =
                updatedRatings[rating as 1 | 2 | 3 | 4 | 5].cardIds.size;

            const newStats: SessionStats = {
                ...prev,
                // cardsStudied conta TODAS as respostas, incluindo repetições
                cardsStudied: prev.cardsStudied + 1,
                ratings: updatedRatings,
                cardsLearned: prev.cardsLearned,
                cardsReviewed: prev.cardsReviewed,
                cardsMasteredToday: prev.cardsMasteredToday,
                // ✅ goodAnswersTotal conta TODAS as respostas boas (inclui repetições)
                goodAnswersTotal: rating >= 4 ? prev.goodAnswersTotal + 1 : prev.goodAnswersTotal
            };

            const wasNew = cardStatus === 'new' || cardStatus === 'saved';
            if (wasNew && rating >= 3) {
                newStats.cardsLearned++;
            } else if (!wasNew) {
                newStats.cardsReviewed++;
            }

            // Dominado = duas notas 5 consecutivas
            // previousRating === 5 significa que a resposta anterior era nota 5
            // rating === 5 significa que esta resposta também é nota 5
            // Duas notas 5 seguidas = dominado
            if (rating === 5 && previousRating === 5) {
                newStats.cardsMasteredToday++;
            }

            console.log('📊 [STATS] Novo estado:', {
                cardsStudied: newStats.cardsStudied,
                cardsMasteredToday: newStats.cardsMasteredToday,
                goodAnswersTotal: newStats.goodAnswersTotal,
            });

            return newStats;
        });
    }, []);

    // =====================================================================
    // CÁLCULOS (para exibição na sidebar — usa Sets, ignora repetições)
    // =====================================================================

    const calculatePrecision = useCallback(() => {
        const weights: Record<number, number> = { 1: 0, 2: 25, 3: 50, 4: 75, 5: 100 };
        let totalWeighted = 0;
        let total = 0;
        for (let r = 1; r <= 5; r++) {
            const count = sessionStats.ratings[r as 1 | 2 | 3 | 4 | 5].count;
            totalWeighted += count * weights[r];
            total += count;
        }
        return total > 0 ? Math.round(totalWeighted / total) : 0;
    }, [sessionStats.ratings]);

    const calculateCorrectAnswers = useCallback(() => {
        return sessionStats.ratings[4].count + sessionStats.ratings[5].count;
    }, [sessionStats.ratings]);

    const resetStats = useCallback(() => {
        const now = Date.now();
        setSessionStats({
            cardsStudied: 0,
            cardsMasteredToday: 0,
            cardsLearned: 0,
            cardsReviewed: 0,
            ratings: {
                1: { count: 0, cardIds: new Set<string>() },
                2: { count: 0, cardIds: new Set<string>() },
                3: { count: 0, cardIds: new Set<string>() },
                4: { count: 0, cardIds: new Set<string>() },
                5: { count: 0, cardIds: new Set<string>() }
            },
            startTime: now,
            goodAnswersTotal: 0
        });
        lastRecordedAtRef.current = now;
        lastRecordedCardsRef.current = 0;
        lastRecordedMasteredRef.current = 0;
        lastRecordedGoodRef.current = 0;
        isRecordingRef.current = false;
    }, []);

    // =====================================================================
    // HELPER: calcular payload incremental
    // Centraliza a lógica para não duplicar entre recordStudySession e cleanup
    // =====================================================================

    const buildIncrementalPayload = (current: SessionStats, cfg: typeof configRef.current, now: number) => {
        const newCardsStudied = current.cardsStudied - lastRecordedCardsRef.current;
        // ✅ BUG 5 CORRIGIDO: delta de dominados, não o total
        const newMastered = current.cardsMasteredToday - lastRecordedMasteredRef.current;
        // ✅ BUG 6 CORRIGIDO: accuracy baseada em cardsStudied (conta repetições)
        // usa o delta para manter consistência com o cardsStudied incremental
        const newGood = current.goodAnswersTotal - lastRecordedGoodRef.current;
        const accuracy = newCardsStudied > 0 ? Math.round((newGood / newCardsStudied) * 100) : 0;

        const timeSpentSeconds = Math.round((now - lastRecordedAtRef.current) / 1000);
        const sessionDeckId = cfg.isSavedMode ? 'saved' : (cfg.deckId || 'saved');
        const sessionSubdeckId = cfg.isSavedMode ? 'saved' : (cfg.subdeckId || 'saved');

        return {
            newCardsStudied,
            payload: {
                deckId: sessionDeckId,
                subdeckId: sessionSubdeckId,
                cardsStudied: newCardsStudied,
                cardsMasteredToday: newMastered,       // ✅ INCREMENTAL
                cardsLearned: current.cardsLearned,
                cardsReviewed: current.cardsReviewed,
                timeSpentSeconds,
                accuracy,                               // ✅ 0–100 garantido
                sessionStartedAt: new Date(lastRecordedAtRef.current),
                sessionEndedAt: new Date(now)
            }
        };
    };

    // =====================================================================
    // FUNÇÃO CENTRAL DE REGISTRO
    // =====================================================================

    const recordStudySession = useCallback(async () => {
        if (isRecordingRef.current) {
            console.log('⏸️ [RECORD] Já registrando, ignorando chamada simultânea');
            return;
        }

        const current = sessionStatsRef.current;
        const cfg = configRef.current;
        const now = Date.now();

        const { newCardsStudied, payload } = buildIncrementalPayload(current, cfg, now);

        if (newCardsStudied <= 0) {
            console.log('⚠️ [RECORD] Sem cards novos para registrar');
            return;
        }

        console.log(`📊 [RECORD] Registrando ${newCardsStudied} cards (total acum.: ${current.cardsStudied})`);
        console.log('📡 [RECORD] Payload:', payload);

        isRecordingRef.current = true;

        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch('/api/stats/record-study', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                console.log('✅ [RECORD] Registrado com sucesso');
                lastRecordedAtRef.current = now;
                lastRecordedCardsRef.current = current.cardsStudied;
                // ✅ Avançar os dois novos marcadores também
                lastRecordedMasteredRef.current = current.cardsMasteredToday;
                lastRecordedGoodRef.current = current.goodAnswersTotal;
            } else {
                const errText = await response.text();
                console.error('❌ [RECORD] Erro:', response.status, errText);
            }
        } catch (error) {
            console.error('❌ [RECORD] Exceção:', error);
        } finally {
            isRecordingRef.current = false;
        }
    }, []);

    // =====================================================================
    // AUTO-REGISTRO A CADA 5 CARDS
    // =====================================================================

    useEffect(() => {
        if (sessionStats.cardsStudied > 0 && sessionStats.cardsStudied % 5 === 0) {
            recordStudySession();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionStats.cardsStudied]);

    // =====================================================================
    // CLEANUP AO DESMONTAR
    // =====================================================================

    useEffect(() => {
        return () => {
            console.log('🔄 [CLEANUP] Desmontando — enviando estatísticas finais...');

            const current = sessionStatsRef.current;
            const cfg = configRef.current;
            const now = Date.now();

            const { newCardsStudied, payload } = buildIncrementalPayload(current, cfg, now);

            if (newCardsStudied <= 0) {
                console.log('⚠️ [CLEANUP] Sem cards pendentes');
                return;
            }

            console.log(`📡 [CLEANUP] Enviando ${newCardsStudied} cards pendentes`);

            const token = localStorage.getItem('accessToken');

            fetch('/api/stats/record-study', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload),
                keepalive: true
            }).catch(() => { });
        };
    }, []);

    // =====================================================================
    // RETORNO
    // =====================================================================

    return {
        sessionStats,
        precision: calculatePrecision(),
        correctAnswers: calculateCorrectAnswers(),
        updateStats,
        resetStats,
        recordStudySession,
        hasRecorded: lastRecordedCardsRef.current > 0
    };
}